import React, { useEffect, useState } from 'react';
import { X, Keyboard, Command } from 'lucide-react';
import { Button } from './ui/Button';
import { useTranslation } from '../lib/i18n';

interface Shortcut {
  keys: string[];
  descKey: string;
  categoryKey: string;
}

const shortcuts: Shortcut[] = [
  { keys: ['?'], descKey: 'shortcuts.showShortcuts', categoryKey: 'shortcuts.catGeneral' },
  { keys: ['Ctrl', 'S'], descKey: 'shortcuts.saveDocument', categoryKey: 'shortcuts.catEditing' },
  { keys: ['Ctrl', 'Z'], descKey: 'shortcuts.undo', categoryKey: 'shortcuts.catEditing' },
  { keys: ['Ctrl', 'Y'], descKey: 'shortcuts.redo', categoryKey: 'shortcuts.catEditing' },
  { keys: ['Ctrl', 'Shift', 'Z'], descKey: 'shortcuts.redoAlt', categoryKey: 'shortcuts.catEditing' },
  { keys: ['Ctrl', 'A'], descKey: 'shortcuts.selectAll', categoryKey: 'shortcuts.catEditing' },
  { keys: ['Ctrl', 'C'], descKey: 'shortcuts.copy', categoryKey: 'shortcuts.catEditing' },
  { keys: ['Ctrl', 'V'], descKey: 'shortcuts.paste', categoryKey: 'shortcuts.catEditing' },
  { keys: ['Ctrl', 'X'], descKey: 'shortcuts.cut', categoryKey: 'shortcuts.catEditing' },
  { keys: ['Ctrl', 'F'], descKey: 'shortcuts.find', categoryKey: 'shortcuts.catNavigation' },
  { keys: ['Esc'], descKey: 'shortcuts.closeDialogs', categoryKey: 'shortcuts.catGeneral' },
];

const KeyboardShortcutsHelp: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          setIsOpen(true);
        }
      }

      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen]);

  if (!isOpen) return null;

  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    const cat = t(shortcut.categoryKey);
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(shortcut);
    return acc;
  }, {} as Record<string, Shortcut[]>);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
              <Keyboard className="w-6 h-6 text-blue-600 dark:text-blue-300" />
            </div>
            <h2 className="text-2xl font-bold">{t('shortcuts.title')}</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <div key={category}>
              <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                {category}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span className="text-gray-700 dark:text-gray-300">
                      {t(shortcut.descKey)}
                    </span>
                    <div className="flex items-center space-x-1">
                      {shortcut.keys.map((key, keyIndex) => (
                        <React.Fragment key={keyIndex}>
                          {keyIndex > 0 && (
                            <span className="text-gray-400 mx-1">+</span>
                          )}
                          <kbd className="px-3 py-1.5 text-sm font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                            {key === 'Ctrl' && (navigator.platform.includes('Mac') ? '⌘' : key)}
                            {key !== 'Ctrl' && key}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-start space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <Command className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                {t('shortcuts.pressToView', { key: '?' })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
